(function sccBlogModule(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.SCCBlog = api;
  if (root.document) {
    if (root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', api.init, { once: true });
    } else {
      api.init();
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createSccBlog() {
  'use strict';

  const RECIPES = Object.freeze({
    informational: [],
    'furniture-intent': ['collection-portal'],
    'how-to': ['compact-product'],
    'product-adjacent': ['collection-portal', 'compact-product'],
    'best-of': ['collection-portal', 'product-row'],
  });

  function recipeFor(intent, hasFeaturedProduct) {
    const recipe = [...(RECIPES[intent] || RECIPES.informational)];

    if (intent === 'product-adjacent' && hasFeaturedProduct) {
      recipe[1] = 'curated-product';
    } else if (intent === 'best-of' && hasFeaturedProduct) {
      recipe.push('curated-product');
    }

    return recipe;
  }

  function capacityForWords(wordCount) {
    if (wordCount >= 1400) return 3;
    if (wordCount >= 700) return 2;
    return 1;
  }

  function effectiveRecipe(intent, hasFeaturedProduct, wordCount) {
    return recipeFor(intent, hasFeaturedProduct).slice(0, capacityForWords(wordCount));
  }

  function selectPlacementIndexes(boundaries, options) {
    const maxBlocks = Math.max(0, Number(options.maxBlocks) || 0);
    const totalChildren = Math.max(0, Number(options.totalChildren) || 0);
    const minimumIndex = Math.ceil(totalChildren * 0.15);
    const blockedIndexes = (options.blockedIndexes || []).map(Number);
    const selected = [];

    for (const rawIndex of boundaries) {
      const index = Number(rawIndex);
      if (!Number.isInteger(index) || index < minimumIndex || index >= totalChildren) continue;
      if (blockedIndexes.some((blocked) => Math.abs(blocked - index) < 4)) continue;
      if (selected.some((placed) => Math.abs(placed - index) < 4)) continue;
      selected.push(index);
      if (selected.length === maxBlocks) break;
    }

    return selected;
  }

  function baseHeadingId(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'section';
  }

  function headingIds(headings) {
    const seen = new Map();
    return headings.map((heading) => {
      const base = baseHeadingId(heading);
      const count = (seen.get(base) || 0) + 1;
      seen.set(base, count);
      return count === 1 ? base : `${base}-${count}`;
    });
  }

  function shouldCreateToc(h2Count, hasExistingToc) {
    return false;
  }

  function planEnhancements(input) {
    const createToc = shouldCreateToc(input.h2Count, input.hasExistingToc);
    if (!input.hasCollection) {
      return { createToc, placements: [], roles: [], showBottomLink: false };
    }

    const roles = effectiveRecipe(input.intent, input.hasFeaturedProduct, input.wordCount);
    const placements = selectPlacementIndexes(input.boundaries, {
      blockedIndexes: input.blockedIndexes,
      maxBlocks: roles.length,
      totalChildren: input.childCount,
    });

    return {
      createToc,
      placements,
      roles: roles.slice(0, placements.length),
      showBottomLink: true,
    };
  }

  function prepareHeadingIds(headings) {
    const generated = headingIds(headings.map((heading) => heading.textContent.trim()));
    const used = new Set();

    headings.forEach((heading, index) => {
      let id = heading.id || generated[index];
      let suffix = 2;
      const base = id;
      while (used.has(id)) id = `${base}-${suffix++}`;
      heading.id = id;
      heading.classList.add('scc-heading-anchor');
      used.add(id);
    });
  }

  function adoptExistingToc(article) {
    let candidate = article.querySelector(
      '.scc-toc, .table-of-contents, .toc-container, [data-toc], #table-of-contents',
    );
    if (!candidate) {
      candidate = [...article.querySelectorAll('h1,h2,h3,h4,h5,h6')].find((heading) => {
        const label = heading.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
        const adjacentList = heading.nextElementSibling;
        return ['table of contents', 'contents', 'in this guide', 'on this page'].includes(label)
          && adjacentList
          && /^(OL|UL)$/.test(adjacentList.tagName);
      }) || null;
    }
    if (!candidate) return null;

    const isHeadingMarker = /^H[1-6]$/.test(candidate.tagName);
    const adjacentList = isHeadingMarker ? candidate.nextElementSibling : null;
    if (!isHeadingMarker || !adjacentList || !/^(OL|UL)$/.test(adjacentList.tagName)) {
      candidate.classList.add('scc-toc');
      return candidate;
    }

    const nav = article.ownerDocument.createElement('nav');
    nav.className = 'scc-toc';
    nav.id = /(?:^|[-_])(toc|table-of-contents)(?:$|[-_])/.test(candidate.id) ? candidate.id : '';
    nav.setAttribute('aria-label', 'On this page');
    const details = article.ownerDocument.createElement('details');
    details.open = true;
    const summary = article.ownerDocument.createElement('summary');
    summary.textContent = 'In this guide';

    candidate.before(nav);
    details.append(summary, adjacentList);
    nav.append(details);
    candidate.remove();
    return nav;
  }

  function wrapTables(article) {
    article.querySelectorAll('table').forEach((table, index) => {
      if (table.parentElement && table.parentElement.classList.contains('scc-table-scroll')) return;
      const wrapper = article.ownerDocument.createElement('div');
      wrapper.className = 'scc-table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', `Scrollable comparison table ${index + 1}`);
      table.before(wrapper);
      wrapper.append(table);
    });
  }

  function articleModel(article) {
    const children = [...article.children];
    const boundaries = [];
    const blockedIndexes = [];
    const blockedSelector = '.scc-toc, [data-scc-capture], .omnisend-form, .klaviyo-form, [data-form-id]';

    children.forEach((child, index) => {
      if (/^H[23]$/.test(child.tagName)) boundaries.push(index);
      if (child.matches(blockedSelector) || child.querySelector(blockedSelector)) blockedIndexes.push(index);
    });

    return { blockedIndexes, boundaries, childCount: children.length, children };
  }

  function insertCommerce(article, layer, roles, placements) {
    const model = articleModel(article);
    const pairs = roles.map((role, index) => ({ role, placement: placements[index] }));

    pairs.reverse().forEach(({ role, placement }) => {
      const template = layer.querySelector(`template[data-scc-role="${role}"]`);
      const target = model.children[placement];
      const element = template && template.content && template.content.firstElementChild
        ? template.content.firstElementChild.cloneNode(true)
        : null;
      if (!element || !target) return;
      annotateCommerceBlock(element, role, placement);
      target.before(element);
    });
  }

  function annotateCommerceBlock(element, role, placement) {
    element.dataset.sccCommerceBlock = role;
    element.dataset.sccBlockSlot = String(placement);
  }

  // Kalles wraps the article body in `.post-content > article.rtet4` and renders the
  // layer inside that wrapper. Sleek/Modiva has a single `.article__content.rte` div and
  // renders the layer as its next sibling, so both shapes are resolved here.
  const CONTAINER_SELECTORS = ['.post-content', '.article__content'];
  const ARTICLE_SELECTORS = ['article.rtet4', '.article__content'];

  function resolveArticleRoot(layer) {
    for (const selector of CONTAINER_SELECTORS) {
      const container = layer.closest(selector);
      if (!container) continue;
      const article = container.matches(ARTICLE_SELECTORS.join(','))
        ? container
        : container.querySelector(ARTICLE_SELECTORS.join(','));
      if (article) return { article, container };
    }

    const parent = layer.parentElement;
    const sibling = parent && parent.querySelector('.article__content');
    if (sibling) return { article: sibling, container: sibling };

    return null;
  }

  function enhanceLayer(layer) {
    if (!layer || layer.dataset.sccInitialized === 'true') return;
    const root = resolveArticleRoot(layer);
    if (!root) return;
    const { article, container: postContent } = root;

    layer.dataset.sccInitialized = 'true';
    postContent.classList.add('scc-blog-enhanced');
    article.classList.add('scc-article');
    wrapTables(article);

    const existingToc = adoptExistingToc(article);
    const headings = [...article.querySelectorAll('h2')].filter((heading) => !heading.closest('.scc-toc'));
    prepareHeadingIds(headings);

    const hasCollection = layer.dataset.hasCollection === 'true';
    const hasFeaturedProduct = layer.dataset.hasFeaturedProduct === 'true';
    let model = articleModel(article);
    let plan = planEnhancements({
      ...model,
      h2Count: headings.length,
      hasCollection,
      hasExistingToc: Boolean(existingToc),
      hasFeaturedProduct,
      intent: layer.dataset.blogType || 'informational',
      wordCount: Number(layer.dataset.wordCount) || 0,
    });

    insertCommerce(article, layer, plan.roles, plan.placements);
    const bottomLink = layer.querySelector('[data-scc-bottom-link]');
    if (bottomLink) bottomLink.hidden = !plan.showBottomLink;
  }

  function init(documentRef) {
    const scope = documentRef && documentRef.querySelectorAll ? documentRef : document;
    scope.querySelectorAll('.scc-blog-layer').forEach(enhanceLayer);
  }

  return {
    annotateCommerceBlock,
    capacityForWords,
    effectiveRecipe,
    headingIds,
    init,
    planEnhancements,
    recipeFor,
    selectPlacementIndexes,
    shouldCreateToc,
  };
});

// Key takeaways (2026-09-27): tag the opening blockquote holding a heading (or a bold-only
// paragraph) directly followed by a list, so scc-blog.css can style it as a summary card.
// Matched by structure, not wording, so translated headings match. Lists of #anchor links are
// TOCs and are skipped; editor-indented lists (ul > li > ul) are stepped into. Class-tagging only.
;(function sccTakeaways(doc) {
  'use strict';
  if (!doc || !doc.querySelector) return;
  function tag() {
    var article = doc.querySelector('.scc-article') || doc.querySelector('.article__content');
    if (!article || article.querySelector('.scc-takeaways')) return;
    var kids = Array.prototype.slice.call(article.children);
    var limit = Math.max(8, Math.ceil(kids.length * 0.25));
    for (var i = 0; i < kids.length && i < limit; i++) {
      var box = kids[i].tagName === 'BLOCKQUOTE' ? kids[i]
        : (kids[i].tagName === 'DIV' ? kids[i].querySelector('blockquote') : null);
      if (!box) continue;
      var head = box.firstElementChild;
      var list = head && head.nextElementSibling;
      var boldOnly = head && head.tagName === 'P' && head.children.length === 1 &&
        /^(STRONG|B)$/.test(head.children[0].tagName) &&
        head.textContent.trim() === head.children[0].textContent.trim();
      if (!head || !(/^H[2-4]$/.test(head.tagName) || boldOnly)) return;
      if (!list || !/^(UL|OL)$/.test(list.tagName) || list.querySelector('a[href^="#"]')) return;
      while (list.children.length === 1 && list.children[0].children.length === 1 &&
             /^(UL|OL)$/.test(list.children[0].children[0].tagName)) {
        list.classList.add('scc-takeaways__shell');
        list = list.children[0].children[0];
      }
      box.classList.add('scc-takeaways');
      head.classList.add('scc-takeaways__title');
      list.classList.add('scc-takeaways__list');
      return;
    }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', tag, { once: true });
  else tag();
})(typeof document !== 'undefined' ? document : null);
